import React, { useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

// MUI
import {
    Stack,
    Typography,
    Breadcrumbs,
    IconButton,
    TextField,
    Grid2,
    Paper,
    Button,
    Link,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    ThemeProvider,
    Fade,
    Box
} from '@mui/material'

// Icons
import {
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Folder as FolderIcon,
    InsertDriveFile as FileIcon,
    Article as ArticleIcon,
    Description as WordIcon,
    TableChart as SpreadsheetIcon,
    PictureAsPdf as PdfIcon,
    Upload as UploadIcon,
    CreateNewFolder as CreateNewFolderIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material'

// Auth
import { useAuthInfo } from '@propelauth/react'

// Components
import { ArtosTheme } from '../../components/Themes'
import PDFWebViewComponent from '../../components/PDFWebViewComponent'

// Utils
import {
    uploadMultipleFilesUpdated,
    listAllFiles,
    createFolder,
    combine
} from '../../utils/utils'

// Contexts
import { useDemo } from '../../contexts/DemoContext'

const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase()

    switch (extension) {
        case 'doc':
        case 'docx':
            return <WordIcon />
        case 'csv':
        case 'xlsx':
        case 'xls':
            return <SpreadsheetIcon />
        case 'pdf':
            return <PdfIcon />
        case 'rtf':
            return <ArticleIcon />
        default:
            return <FileIcon />
    }
}

// const documentTypes = [
//     { name: 'Protocol', regex: protocolRegex, priority: 4, color: 'gray', textColor: 'white' },
//     { name: 'Table of Lists', regex: tolRegex, priority: 3, color: 'gray', textColor: 'white' },
//     { name: 'Statistical Analysis Plan', regex: sapRegex, priority: 2, color: 'gray', textColor: 'white' },
//     { name: 'Clinical Study Report', regex: csrRegex, priority: 1, color: 'gray', textColor: 'white' },
// ]

// const defaultDocumentType = { name: 'Tables, Figures, and Lists', regex: '', priority: 5, color: 'red', textColor: 'white' }

// const rootFolders = [
//     'input',
//     'templates',
//     'output'
// ]
const rootFolders = [
    'input',
    'templates'
]

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'artos_file_cache_'

// Cache helper functions
const cacheHelpers = {
    getCacheKey: (orgId, folderPath) => {
        return `${CACHE_KEY_PREFIX}${orgId}_${folderPath}`
    },
    
    getFromCache: (orgId, folderPath) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId, folderPath)
            const cached = localStorage.getItem(cacheKey)
            
            if (!cached) return null
            
            const parsedCache = JSON.parse(cached)
            const now = Date.now()
            
            // Check if cache is expired
            if (now - parsedCache.timestamp > CACHE_TTL) {
                localStorage.removeItem(cacheKey)
                return null
            }
            
            return parsedCache.data
        } catch (error) {
            console.error('Error reading from cache:', error)
            return null
        }
    },
    
    saveToCache: (orgId, folderPath, data) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId, folderPath)
            const cacheData = {
                data,
                timestamp: Date.now()
            }
            localStorage.setItem(cacheKey, JSON.stringify(cacheData))
        } catch (error) {
            console.error('Error saving to cache:', error)
            // If localStorage is full, clear old cache entries
            if (error.name === 'QuotaExceededError') {
                cacheHelpers.clearOldCacheEntries()
            }
        }
    },
    
    clearOldCacheEntries: () => {
        const now = Date.now()
        const keysToRemove = []
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                try {
                    const cached = JSON.parse(localStorage.getItem(key))
                    if (now - cached.timestamp > CACHE_TTL) {
                        keysToRemove.push(key)
                    }
                } catch (error) {
                    keysToRemove.push(key)
                }
            }
        }
        
        keysToRemove.forEach(key => localStorage.removeItem(key))
    },
    
    clearAllCache: () => {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
    }
}

function SourceDocumentsPage() {
    const authInfo = useAuthInfo()
    const orgs = authInfo.orgHelper.getOrgs()
    const orgId = orgs[0].orgId
    const org_name = orgs[0].orgName

    const [currentPath, setCurrentPath] = useState(`/${orgId}`)
    const [currentContainer, setCurrentContainer] = useState("")
    const [searchQuery, setSearchQuery] = useState('')
    const [previewFile, setPreviewFile] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadSuccess, setUploadSuccess] = useState(null)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [isDragging, setIsDragging] = useState(false)
    const [isUploadMode, setIsUploadMode] = useState(false)
    const fileInputRef = React.useRef(null)
    const { isDemo } = useDemo()
    const [orderBy, setOrderBy] = useState('name')
    const [order, setOrder] = useState('asc')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(false)
    const [groupedFiles, setGroupedFiles] = useState([])
    const [inputFiles, setInputFiles] = useState([])
    const [isLoadingInputFiles, setIsLoadingInputFiles] = useState(false)
    const [isCreatingFolder, setIsCreatingFolder] = useState(false)
    const [error, setError] = useState(null)
    const [showCreateFolder, setShowCreateFolder] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')

    const [rowIndex, setRowIndex] = useState(-1)
    const [columnIndex, setColumnIndex] = useState(-1)

    const [open, setOpen] = useState(false)

    const [refresh, setRefresh] = useState(0)

    // Add state for tracking fetched folders
    const [fetchedFolders, setFetchedFolders] = useState(new Set())
    const [folderCache, setFolderCache] = useState({})

    // function getDocumentType(fileName) {
    //     // console.log('documentTypes', documentTypes)
    //     // console.log('fileName', fileName)
    //     const documentType = documentTypes.find((type) => {
    //         const regex = new RegExp(type.regex, 'i')
    //         // console.log('regex', regex)
    //         // console.log('fileName', fileName)
    //         // console.log('regex.test(fileName)', regex.test(fileName))
    //         return regex.test(fileName)
    //     })
    //     // console.log('documentType', documentType)
    //     return documentType || defaultDocumentType
    // }

    async function getOrgAccessToken() {
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
        return orgAccessToken
    }

    const processFiles = React.useMemo(() => {
        const resultWithRandomDocumentTypeAndId = groupedFiles.map((root) => {
            return root.files
        }).flat().map((file) => {
            if (file.id) {
                return file
            }
            return {
                ...file,
                // document_type: getDocumentType(file.name),
                id: uuidv4(),
            }
        })
        
        console.log('resultWithRandomDocumentTypeAndId', resultWithRandomDocumentTypeAndId)
        
        return resultWithRandomDocumentTypeAndId
    }, [groupedFiles])

    useEffect(() => {
        setInputFiles(processFiles)
    }, [processFiles])

    // Initialize with empty root folders (lazy loading)
    useEffect(() => {
        const initialGroupedFiles = rootFolders.map(folder => ({
            id: uuidv4(),
            folder: folder,
            files: [] // Start with empty files array
        }))
        setGroupedFiles(initialGroupedFiles)
    }, [])

    // Function to fetch a specific folder's contents
    async function fetchFolderContents(folderPath, forceRefresh = false) {
        const normalizedPath = folderPath.replace(/^\//, '').replace(`${orgId}/`, '')
        
        // Check cache first (unless force refresh is requested)
        if (!forceRefresh) {
            const cachedData = cacheHelpers.getFromCache(orgId, normalizedPath)
            if (cachedData) {
                // Update state with cached data
                setGroupedFiles(prev => prev.map(root => {
                    if (root.folder === normalizedPath) {
                        return { ...root, files: cachedData }
                    }
                    return root
                }))
                setFetchedFolders(prev => new Set([...prev, normalizedPath]))
                return
            }
        }
        
        // Check if we've already fetched this folder and it's not a force refresh
        if (!forceRefresh && fetchedFolders.has(normalizedPath)) {
            return
        }

        setIsLoadingInputFiles(true)
        try {
            const orgAccessToken = await getOrgAccessToken()
            const files = await listAllFiles(normalizedPath, orgAccessToken)
            
            // Update the grouped files with the fetched content
            setGroupedFiles(prev => prev.map(root => {
                if (root.folder === normalizedPath) {
                    return { ...root, files }
                }
                return root
            }))
            
            // Mark this folder as fetched
            setFetchedFolders(prev => new Set([...prev, normalizedPath]))
            
            // Save to localStorage cache
            cacheHelpers.saveToCache(orgId, normalizedPath, files)
            
            // Also update in-memory cache
            setFolderCache(prev => ({
                ...prev,
                [normalizedPath]: {
                    files,
                    timestamp: Date.now()
                }
            }))
        } catch (error) {
            console.error(`Failed to fetch folder ${normalizedPath}:`, error)
            setError(`Failed to load folder: ${normalizedPath}`)
        } finally {
            setIsLoadingInputFiles(false)
        }
    }

    // Remove the old useEffect that fetched all folders
    // useEffect(() => {
    //     async function retrieveInputs() {
    //         ...
    //     }
    //     ...
    // }, [])

    // Process files and folders based on current path
    const { files, folders } = React.useMemo(() => {
        const currentFiles = []
        const currentFolders = new Set()

        // If we're at the org root, show the root folders
        if (currentPath === `/${orgId}`) {
            return {
                files: [],
                folders: rootFolders
            }
        }

        if (!inputFiles || !Array.isArray(inputFiles)) {
            return { files: [], folders: [] }
        }

        inputFiles.forEach((file) => {
            if (!file || !file.name) return

            const filePath = file.name
            const normalizedCurrentPath = currentPath.replace(/^\//, '')
            const normalizedFilePath = filePath.replace(/^\//, '')

            if (normalizedFilePath.startsWith(normalizedCurrentPath)) {
                let relativePath = normalizedFilePath.slice(
                    normalizedCurrentPath.length
                )
                relativePath = relativePath.replace(/^\//, '')

                if (!relativePath) return

                const parts = relativePath.split('/')

                if (parts.length > 1) {
                    currentFolders.add(parts[0])
                } else {
                    currentFiles.push(file)
                }
            }
        })

        return {
            files: currentFiles,
            folders: Array.from(currentFolders),
        }
    }, [inputFiles, currentPath, orgId])

    // Filter based on search
    const filteredItems = React.useMemo(() => {
        if (!searchQuery) return { files, folders }

        console.log('groupedFiles', groupedFiles)
        const allFilesForRootFolder = groupedFiles.filter((root) => {
            console.log('root', root)
            console.log('currentPath', currentPath)
            console.log('currentPath.includes(root.folder)', currentPath.includes(root.folder))
            return currentPath.includes(root.folder)
        }).map((root) => {
            return root.files
        }).flat()

        console.log('allFilesForRootFolder', allFilesForRootFolder)

        const filteredObjects = allFilesForRootFolder.filter((f) => {
            return f.name.toLowerCase().includes(searchQuery.toLowerCase())
        })

        console.log('filteredObjects', filteredObjects)

        const filteredFiles = filteredObjects.filter((f) => {
            const lastCharacter = f.name.charAt(f.name.length - 1)
            console.log('lastCharacter', lastCharacter)
            return lastCharacter !== '/'
        })
        
        console.log('filteredFiles', filteredFiles)

        return {
            files: filteredFiles,
            folders: [],
        }
    }, [files, folders, searchQuery])

    const handleFolderClick = (folderName) => {
        const newPath = `${currentPath}/${folderName}`
        setCurrentPath(newPath)
        
        // Check if this is a root folder and fetch its contents if needed
        const pathParts = newPath.split('/').filter(Boolean)
        if (pathParts.length === 2 && rootFolders.includes(folderName)) {
            fetchFolderContents(folderName)
        }
    }

    const handleBreadcrumbClick = (index) => {
        const parts = currentPath.split('/').filter(Boolean)
        const updatedPath = "/" + parts.slice(0, index + 2).join('/')
        setCurrentPath(updatedPath)
    }

    const handleBackClick = () => {
        const parts = currentPath.split('/').filter(Boolean)
        console.log('parts', parts)
        if (parts.length > 1) {
            // Keep at least /{orgId}
            parts.pop()
            setCurrentPath("/" + parts.join('/'))
        }
    }

    const handleUploadClick = () => {
        setIsUploadMode(true)
        // setCurrentPath(`/${orgId}/input`)
        setPreviewFile(null)
        setIsPreviewExpanded(false)
    }

    const handleCancelUpload = () => {
        setIsUploadMode(false)
        setIsDragging(false)
    }

    const handleFileUpload = async (files, fileContents) => {
        const rootFolder = groupedFiles.find((root) => displayPathParts.includes(root.folder))
        const currentFolder = displayPathParts.slice(1).join('/')
        
        console.log('rootFolder', rootFolder)
        console.log('currentFolder', currentFolder)
        
        try {
            setIsUploading(true)

            const orgAccessToken = await getOrgAccessToken()

            await uploadMultipleFilesUpdated(
                setIsUploading,
                files.map((file, index) => ({
                    name: file.name,
                    content: fileContents[index],
                })),
                fileContents,
                (error) => {
                    console.error('Upload error:', error)
                    setUploadSuccess(
                        'Failed to upload files. Please try again.'
                    )
                },
                currentFolder,
                orgAccessToken
            )

            // Clear cache for the affected folder
            const folderToClear = rootFolder ? rootFolder.folder : currentFolder
            const cacheKey = cacheHelpers.getCacheKey(orgId, folderToClear)
            localStorage.removeItem(cacheKey)

            const newFileNames = files.map((f) => f.name)
            setUploadedFiles(newFileNames)

            const updatedFiles = await listAllFiles(currentFolder, orgAccessToken)
            
            // Update cache with new files
            cacheHelpers.saveToCache(orgId, folderToClear, updatedFiles)
            
            const updatedGroupedFiles = groupedFiles.map((root) => {
                if (root.folder === rootFolder.folder) {
                    return {
                        ...root,
                        files: combine(root.files, updatedFiles, 'name')
                    }
                }
                return root
            })

            setGroupedFiles(updatedGroupedFiles)
            setUploadSuccess(
                `Successfully uploaded ${files.length} file${
                    files.length > 1 ? 's' : ''
                }`
            )

            setIsUploadMode(false)
            
            setTimeout(() => {
                setUploadSuccess(null)
            }, 6000)
        } catch (error) {
            console.error('Upload failed:', error)
            setUploadSuccess('Failed to upload files. Please try again.')
        } finally {
            setIsUploading(false)
        }
    }

    const isNewlyUploadedFile = (fileName) => {
        return uploadedFiles.includes(fileName.split('/').pop())
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = async (e) => {
        e.preventDefault()
        setIsDragging(false)

        const files = Array.from(e.dataTransfer.files)
        const fileContents = await Promise.all(
            files.map(
                (file) =>
                    new Promise((resolve) => {
                        const reader = new FileReader()
                        reader.onload = (e) => resolve(e.target.result)
                        reader.readAsArrayBuffer(file)
                    })
            )
        )

        handleFileUpload(files, fileContents)
    }

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files)
        const fileContents = await Promise.all(
            files.map(
                (file) =>
                    new Promise((resolve) => {
                        const reader = new FileReader()
                        reader.onload = (e) => resolve(e.target.result)
                        reader.readAsArrayBuffer(file)
                    })
            )
        )

        handleFileUpload(files, fileContents)
        e.target.value = ''
    }

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const sortFiles = (files) => {
        return [...files].sort((a, b) => {
            const aName = a.name.split('/').pop().toLowerCase()
            const bName = b.name.split('/').pop().toLowerCase()

            // Safely handle priority values that might be undefined
            const aTypePriority = a.document_type?.priority ?? 999
            const bTypePriority = b.document_type?.priority ?? 999

            // If sorting by name
            if (orderBy === 'name') {
                return order === 'asc'
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName)
            }
            // If sorting by type
            else if (orderBy === 'type') {
                // First compare by priority if available
                if (aTypePriority !== bTypePriority) {
                    return order === 'asc'
                        ? aTypePriority - bTypePriority
                        : bTypePriority - aTypePriority
                }
                // If priorities are the same, sort by type name as secondary criteria
                return order === 'asc'
                    ? a.document_type.name.localeCompare(b.document_type.name)
                    : b.document_type.name.localeCompare(a.document_type.name)
            }
            return 0
        })
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const paginatedFiles = React.useMemo(() => {
        const startIndex = page * rowsPerPage
        return sortFiles(filteredItems.files).slice(
            startIndex,
            startIndex + rowsPerPage
        )
    }, [filteredItems, sortFiles, page, rowsPerPage, orderBy, order])
    
    const pathParts = currentPath.split('/').filter(Boolean)
    
    const displayPathParts = pathParts.map((part, index) => {
        if (index === 0) {
            return org_name || part
        } else {
            return part
        }
    })

    const handleCreateFolder = async () => {
        setShowCreateFolder(false)
        setIsCreatingFolder(true)
        try {
            const orgAccessToken = await getOrgAccessToken()
            const rootFolder = groupedFiles.find((root) => displayPathParts.includes(root.folder))
            const currentFolder = displayPathParts.slice(1).join('/')

            await createFolder(newFolderName, currentFolder, orgAccessToken)
            setNewFolderName('')

            // Clear cache for the affected folder
            const folderToClear = rootFolder ? rootFolder.folder : currentFolder
            const cacheKey = cacheHelpers.getCacheKey(orgId, folderToClear)
            localStorage.removeItem(cacheKey)

            const updatedFiles = await listAllFiles(rootFolder.folder, orgAccessToken)
            
            // Update cache with new folder structure
            cacheHelpers.saveToCache(orgId, folderToClear, updatedFiles)
            
            const updatedGroupedFiles = groupedFiles.map((root) => {
                if (root.folder === rootFolder.folder) {
                    return {
                        ...root,
                        files: combine(root.files, updatedFiles, 'name')
                    }
                }
                return root
            })

            setGroupedFiles(updatedGroupedFiles)

            setCurrentPath(`/${orgId}/${currentFolder}`)
        } catch (error) {
            console.error('Error creating folder:', error)
        } finally {
            setIsCreatingFolder(false)
        }
    }

    const handleDocumentTypeChange = (index, columnName, event) => {
        
        const value = event.target.value
        const newInputFiles = [...folders, ...paginatedFiles]
        newInputFiles[index+folders.length] = { 
            ...newInputFiles[index+folders.length], 
            [columnName]: value 
        }
        setInputFiles(newInputFiles)
        handleExit()
        // setTimeout(() => {}, 4000)
    }

    const handleExit = () => {
        setRowIndex(-1)
        setColumnIndex(-1)
        setOpen(false)
    }

    const handleOpen = () => {
        setOpen(true)
    }

    const handleClick = (index) => {
        setRowIndex(index)
        setColumnIndex(2)
        handleOpen()
    }

    const handleClose = (e) => {
        e.stopPropagation()
        setOpen(false)
    }

    const isLoading = isLoadingInputFiles || isUploading || isCreatingFolder

    const renderContent = () => {
        if (isPreviewExpanded && previewFile) {
            return (
                <Paper sx={{ height: '100%', overflow: 'hidden' }}>
                    <PDFWebViewComponent 
                        initialFile={previewFile.url} 
                        filename={previewFile.name}
                    />
                </Paper>
            )
        }

        return (
            <Grid2 container spacing={2} sx={{ height: '100%', flexWrap: 'nowrap', padding: 1}}>
                <Grid2
                    size={8}
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    {/* Loading UI */}
                    {isLoading ? (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{ height: '100vh', width: '100%' }}
                        >
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>
                                {isUploading ? 'Uploading files...' : isCreatingFolder ? 'Creating folder...' : 'Loading files...'}
                            </Typography>
                        </Stack>
                    ) : isUploadMode ? (
                        <Paper
                            sx={{
                                height: '100vh',
                                width: '100%',
                                border: '2px dashed #ccc',
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isDragging
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : 'transparent',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadIcon
                                sx={{
                                    fontSize: 48,
                                    color: 'text.secondary',
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h6"
                                color="text.secondary"
                                align="center"
                                sx={{ mb: 1 }}
                            >
                                Click to upload a file or drag and drop
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                                sx={{ textDecoration: 'underline' }}
                            >
                                PDF, DOCX, XLSX, CSV, or RTF up to 100MB
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                                <Table stickyHeader size="small" sx={{ width: '100%' }}>
                                    <TableHead 
                                        sx={{
                                            width: '100%',
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell
                                                width="40px"
                                                padding="checkbox"
                                            ></TableCell>
                                            <TableCell padding="normal" sx={{ width: '100%' }}>
                                                <TableSortLabel
                                                    active={orderBy === 'name'}
                                                    direction={order}
                                                    onClick={() =>
                                                        handleSort('name')
                                                    }
                                                >
                                                    Name
                                                </TableSortLabel>
                                            </TableCell>
                                            {/* <TableCell padding="normal">
                                                <TableSortLabel
                                                    active={orderBy === 'type'}
                                                    direction={order}
                                                    onClick={() =>
                                                        handleSort('type')
                                                    }
                                                >
                                                    Type
                                                </TableSortLabel>
                                            </TableCell> */}
                                            <TableCell
                                                align="center"
                                                padding="normal"
                                                sx={{ width: '100%', pr: '24px' }}
                                            >
                                                
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredItems.folders.map((folder) => (
                                            <TableRow
                                                key={folder}
                                                hover
                                                onClick={() =>
                                                    handleFolderClick(folder)
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <FolderIcon color="primary" />
                                                </TableCell>
                                                <TableCell padding="normal">
                                                    {folder}
                                                </TableCell>
                                                <TableCell padding="normal"></TableCell>
                                            </TableRow>
                                        ))}
                                        {paginatedFiles.map((file, index) => (
                                            <TableRow
                                                key={file.id}
                                                hover
                                                sx={{
                                                    transition: 'all 0.2s ease',
                                                    border: isNewlyUploadedFile(
                                                        file.name
                                                    )
                                                        ? '1px solid #1976d2'
                                                        : 'none',
                                                    '& .MuiTableCell-root': {
                                                        transition:
                                                            'all 0.2s ease',
                                                    },
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    {getFileIcon(file.name)}
                                                </TableCell>
                                                <TableCell padding="normal">
                                                    {file.name.split('/').pop()}
                                                </TableCell>
                                                {/* <ClickAwayListener
                                                    onClickAway={handleExit}
                                                >
                                                    <TableCell
                                                        padding="normal"
                                                        sx={{
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleClick(index)
                                                        }}
                                                    >
                                                        {rowIndex === index &&
                                                        columnIndex === 2 ? (
                                                            <SingleSelectChip
                                                                key={file.id}
                                                                options={
                                                                    documentTypes
                                                                }
                                                                selection={
                                                                    file.document_type
                                                                }
                                                                handleChange={(
                                                                    event
                                                                ) => {
                                                                    handleDocumentTypeChange(
                                                                        index,
                                                                        'document_type',
                                                                        event
                                                                    )
                                                                }}
                                                                open={open}
                                                                handleOpen={(
                                                                    e
                                                                ) =>
                                                                    handleOpen(
                                                                        e
                                                                    )
                                                                }
                                                                handleClose={
                                                                    handleClose
                                                                }
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={
                                                                    file
                                                                        .document_type
                                                                        .name
                                                                }
                                                                sx={{
                                                                    bgcolor:
                                                                        file
                                                                            .document_type
                                                                            .color,
                                                                    color: file
                                                                        .document_type
                                                                        .textColor,
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        bgcolor:
                                                                            'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                    '&:active':
                                                                        {
                                                                            bgcolor:
                                                                                'rgba(0, 0, 0, 0.04)',
                                                                        },
                                                                    '&:focus': {
                                                                        bgcolor:
                                                                            'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                </ClickAwayListener> */}
                                                <TableCell
                                                    align="right"
                                                    padding="normal"
                                                >
                                                    <Button
                                                        onClick={(e) => {
                                                            setPreviewFile(file)
                                                            setSearchQuery('')
                                                            e.stopPropagation()
                                                        }}
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Preview
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredItems.files.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[25, 50, 100]}
                                sx={{
                                    borderTop:
                                        '1px solid rgba(224, 224, 224, 1)',
                                    flexShrink: 0,
                                }}
                            />
                        </Paper>
                    )}
                </Grid2>
                {previewFile && !isPreviewExpanded && (
                    <Grid2
                        size={4}
                        sx={{
                            height: '100%',
                            width: '50%',
                        }}
                    >
                        <Paper
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <PDFWebViewComponent
                                initialFile={previewFile.url}
                                filename={previewFile.name}
                            />
                        </Paper>
                    </Grid2>
                )}
            </Grid2>
        )
    }

    // Clear cache on unmount (logout)
    useEffect(() => {
        return () => {
            // Optional: Clear cache when component unmounts
            // cacheHelpers.clearAllCache()
        }
    }, [])

    // Add refresh handler
    const handleRefresh = async () => {
        // Clear cache for current folder
        const pathParts = currentPath.split('/').filter(Boolean)
        if (pathParts.length === 2 && rootFolders.includes(pathParts[1])) {
            await fetchFolderContents(pathParts[1], true)
        }
    }

    return (
        <Box sx={{ // Full width container
            width: '100%',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
            height: '100%',
            overflowY: 'auto',
        }}>
            <Box sx={{ // Inner container
                width: '100%', 
                height: '100%', 
                padding: '40px 64px', 
                boxSizing: 'border-box',
                gap: '24px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F9FAFB',
            }}>
                <Box sx={{ // Header container
                    width: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}>
                    <Box sx={{ // Main panel header
                        width: '100%',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}>
                        <Box sx={{ 
                            gap: '8px',
                            display: 'flex', 
                            flexDirection: 'column' 
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <Typography sx={{
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    lineHeight: '36px',
                                    color: '#374151',
                                }}>
                                    Document Library
                                </Typography>   
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#374151',
                                }}>
                                    Manage all your documents here.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                    {/* <Box className="main-panel-header">
                        <h2 className="top-main-panel"> </h2>
                    </Box>
                    {isDemo && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This workflow is a demonstration in a sandbox
                            environment.
                        </Alert>
                    )}
                    <Box
                        className="secondary-header-container"
                        style={isDemo ? { marginTop: '0px' } : {}}
                    >
                        <p className="secondary-text">
                            Manage all your documents here.
                        </p>
                    </Box>
                </Box> */}

                <ThemeProvider theme={ArtosTheme}>
                    <Box
                        sx={{
                            width: '100%',
                            // height: 'calc(100% - 100px)',
                            height: '85%',
                        }}
                    >
                        <Stack spacing={2} sx={{ flexShrink: 0 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <IconButton
                                    onClick={handleBackClick}
                                    disabled={pathParts.length <= 1 || isUploadMode || searchQuery !== ''}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Breadcrumbs sx={{ flexGrow: 1 }}>
                                    <Link
                                        component="button"
                                        onClick={() =>
                                            setCurrentPath(`/${orgId}`)
                                        }
                                        disabled={searchQuery !== ''}
                                        underline="hover"
                                        color="inherit"
                                    >
                                        {org_name || orgId}
                                    </Link>
                                    {currentPath
                                        .split('/')
                                        .filter(Boolean)
                                        .slice(1)
                                        .map((part, index) => (
                                            <Link
                                                key={index}
                                                component="button"
                                                onClick={() =>
                                                    handleBreadcrumbClick(index)
                                                }
                                                disabled={searchQuery !== ''}
                                                underline="hover"
                                                color="inherit"
                                            >
                                                {part}
                                            </Link>
                                        ))}
                                </Breadcrumbs>
                                {previewFile && (
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            setIsPreviewExpanded(
                                                !isPreviewExpanded
                                            )
                                        }
                                    >
                                        {isPreviewExpanded
                                            ? 'Close Preview'
                                            : 'Expand Preview'}
                                    </Button>
                                )}
                                {!isUploadMode ? (
                                    <>
                                        <IconButton
                                            onClick={handleRefresh}
                                            disabled={isLoading || currentPath === `/${orgId}`}
                                            title="Refresh"
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                        <Button
                                            variant="contained"
                                            startIcon={<CreateNewFolderIcon />}
                                            onClick={() =>
                                                setShowCreateFolder(
                                                    (prev) => !prev
                                                )
                                            }
                                            disabled={isLoading || currentPath === `/${orgId}`}
                                        >
                                            Create Folder
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<UploadIcon />}
                                            onClick={handleUploadClick}
                                            disabled={isLoading || currentPath === `/${orgId}` || orgId === '73fcdbb8-39da-48f9-823a-6b864bf3e9a6'}
                                        >
                                            Upload Files
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelUpload}
                                        disabled={isLoading || currentPath === `/${orgId}`}
                                    >
                                        Cancel Upload
                                    </Button>
                                )}
                            </Stack>
                                {showCreateFolder && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <TextField
                                            label="New Folder Name"
                                            value={newFolderName}
                                            onChange={(e) =>
                                                setNewFolderName(e.target.value)
                                            }
                                            size="small"
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleCreateFolder}
                                            disabled={!newFolderName.trim()}
                                        >
                                            Create
                                        </Button>
                                    </Stack>
                                )}
                                {!isPreviewExpanded && !isUploadMode && (
                                    <Fade in={true} unmountOnExit>
                                        <TextField
                                            fullWidth
                                            placeholder="Search in current folder..."
                                            value={searchQuery}
                                            onChange={(e) =>
                                                setSearchQuery(e.target.value)
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <SearchIcon sx={{ mr: 1 }} />
                                                ),
                                            }}
                                            sx={{
                                                px: 1,
                                            }}
                                        />
                                    </Fade>
                                )}
                        </Stack>
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflow: 'hidden',
                                mt: 2,
                                // height: 'calc(100% - 100px)',
                                height: '85%',
                            }}
                        >
                            {renderContent()}
                        </Box>

                        <input
                            type="file"
                            multiple
                            accept=".pdf,.docx,.xlsx,.csv,.rtf"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </Box>
                </ThemeProvider>

                {uploadSuccess && (
                    <Alert
                        severity={
                            uploadSuccess?.includes('Failed')
                                ? 'error'
                                : 'success'
                        }
                        sx={{
                            position: 'fixed',
                            top: 24,
                            right: 24,
                            zIndex: 2000,
                        }}
                        onClose={() => setUploadSuccess(null)}
                    >
                        {uploadSuccess}
                    </Alert>
                )}
            </Box>
        </Box>
    )
}

export default SourceDocumentsPage

